import { useQuery } from "@tanstack/react-query";
import { Card, Drawer, Tag, Typography } from "antd";
import type { TableProps } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiFetch, withQuery } from "../api/client";
import { compareNumeric, compareText } from "../api/format";
import type {
  CongregationSpeechStatisticRow,
  ListResponse,
  SpeechSpeakerStatisticRow,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import { FilterBar } from "../components/FilterBar";
import type { FilterValues } from "../components/FilterBar";

/**
 * Sidebar entry "congregation_speech_statistics" → /congregation/speech_statistics.
 *
 * Which talks have been held here and how often, drilling down into the
 * speakers who held each one — a week at a time, so the same speaker appears
 * once per visit rather than once in total.
 *
 * The list is every talk the congregation may be offered (its circumscription's
 * language, the languages it collaborates in) plus anything already held, not
 * just the ones with a count.
 */
export function CongregationSpeechStatisticsPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<FilterValues>({});
  const [selected, setSelected] = useState<CongregationSpeechStatisticRow | null>(null);

  const speeches = useQuery({
    queryKey: ["congregation", "speech_statistics", filters],
    queryFn: () =>
      apiFetch<ListResponse<CongregationSpeechStatisticRow>>(
        withQuery("/congregation/speech_statistics", filters),
      ),
  });

  const speakers = useQuery({
    queryKey: ["congregation", "speech_statistics", "speakers", selected?.id, filters],
    queryFn: () =>
      apiFetch<ListResponse<SpeechSpeakerStatisticRow>>(
        withQuery(`/congregation/speech_statistics/${selected!.id}/speakers`, {
          speaker_filter: filters.speaker_filter ?? "",
          congregation_filter: filters.congregation_filter ?? "",
          circumscription_filter: filters.circumscription_filter ?? "",
        }),
      ),
    enabled: selected !== null,
  });

  const fields = useMemo(
    () => [
      { name: "speech_filter", label: t("activerecord.models.speech") },
      { name: "language_filter", label: t("activerecord.models.language") },
      { name: "speaker_filter", label: t("activerecord.models.speaker") },
      { name: "congregation_filter", label: t("activerecord.models.congregation") },
      { name: "circumscription_filter", label: t("activerecord.models.circumscription") },
    ],
    [t],
  );

  const columns = useMemo<TableProps<CongregationSpeechStatisticRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.congregation.assignations"),
        dataIndex: "assignation_count",
        width: 110,
        align: "right",
        defaultSortOrder: "descend",
        sorter: (a, b) => a.assignation_count - b.assignation_count,
        render: (value: number) => (value > 0 ? <Tag color="blue">{value}</Tag> : null),
      },
      {
        title: t("activerecord.attributes.speech.schema"),
        dataIndex: "schema",
        width: 100,
        sorter: (a, b) => compareNumeric(a.schema, b.schema),
      },
      {
        title: t("activerecord.attributes.speech.title"),
        dataIndex: "title",
        ellipsis: true,
        sorter: (a, b) => compareText(a.title, b.title),
      },
      {
        title: t("activerecord.attributes.speech.language_id"),
        dataIndex: "language_code",
        width: 100,
        sorter: (a, b) => compareText(a.language_code, b.language_code),
      },
    ],
    [t],
  );

  const speakerColumns = useMemo<TableProps<SpeechSpeakerStatisticRow>["columns"]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 170,
        defaultSortOrder: "descend",
        sorter: (a, b) => compareText(a.week_begin, b.week_begin),
      },
      {
        title: t("activerecord.attributes.speaker.first_name"),
        dataIndex: "first_name",
        width: 140,
      },
      {
        title: t("activerecord.attributes.speaker.last_name"),
        dataIndex: "last_name",
        width: 140,
      },
      {
        title: t("activerecord.attributes.circumscription.short"),
        dataIndex: "circumscription_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.congregation.congregation_code"),
        dataIndex: "congregation_code",
        width: 90,
      },
      {
        title: t("activerecord.models.congregation"),
        dataIndex: "congregation_denomination",
        ellipsis: true,
      },
      { title: t("activerecord.attributes.user.phone"), dataIndex: "phone", width: 140 },
      { title: t("activerecord.attributes.user.mobile"), dataIndex: "mobile", width: 140 },
      {
        title: t("activerecord.attributes.user.private_email"),
        dataIndex: "email",
        width: 200,
        ellipsis: true,
      },
      { title: t("activerecord.attributes.user.zip_code"), dataIndex: "zip_code", width: 90 },
      { title: t("activerecord.attributes.user.country"), dataIndex: "country", width: 90 },
    ],
    [t],
  );

  return (
    <Card title={t("navigation.congregation_speech_statistics")}>
      <FilterBar fields={fields} onApply={setFilters} loading={speeches.isFetching} />

      <DataTable
        columns={columns}
        rows={speeches.data?.data}
        loading={speeches.isPending}
        error={speeches.error}
        exportAs={t("navigation.congregation_speech_statistics")}
        onRowClick={setSelected}
      />

      <Drawer
        open={selected !== null}
        onClose={() => setSelected(null)}
        size={1040}
        destroyOnHidden
        title={
          selected && (
            <Typography.Text>
              {[selected.schema, selected.title].filter(Boolean).join(" – ")}
            </Typography.Text>
          )
        }
      >
        <Typography.Title level={5}>{t("speech.speakers")}</Typography.Title>

        {/* Named after the talk: the file has to say which one was held. */}
        <DataTable
          columns={speakerColumns}
          rows={speakers.data?.data}
          exportAs={[selected?.schema, t("speech.speakers")].filter(Boolean).join(" · ")}
          loading={speakers.isPending}
          error={speakers.error}
          paginated={false}
        />
      </Drawer>
    </Card>
  );
}
